import { Controller, type Control } from "react-hook-form";
import { Input } from "@/components/ui/input";
import CountryCodeCombobox from "./CountryCodeCombobox";
import { DEFAULT_COUNTRY_CODE } from "@/constants/countryCodes";

interface PhoneFieldWithCountryProps {
  control: Control<any>;
  /** Name of the country-code field on the form (default: `phoneCountryCode`). */
  countryCodeName?: string;
  /** Name of the national-number field on the form (default: `phone`). */
  phoneName?: string;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  /** Read-only mode — used by the tenant-wide field lock. */
  disabled?: boolean;
}

/**
 * Phone input split into a country-code combobox + national-number text
 * input, both bound to react-hook-form via Controller. The form must have
 * fields matching `countryCodeName` (e.g. "+91") and `phoneName` (digits
 * only). The country-code dropdown supports search by name, ISO region,
 * or dial code.
 */
export default function PhoneFieldWithCountry({
  control,
  countryCodeName = "phoneCountryCode",
  phoneName = "phone",
  className = "",
  inputClassName = "",
  placeholder = "Mobile number",
  disabled,
}: PhoneFieldWithCountryProps) {
  return (
    <div className={`flex gap-2 w-full ${className}`}>
      <Controller
        name={countryCodeName}
        control={control}
        defaultValue={DEFAULT_COUNTRY_CODE}
        render={({ field, fieldState }) => (
          <CountryCodeCombobox
            value={field.value}
            onChange={field.onChange}
            hasError={!!fieldState.error}
            disabled={disabled}
          />
        )}
      />

      <Controller
        name={phoneName}
        control={control}
        render={({ field, fieldState }) => (
          <Input
            type="tel"
            inputMode="numeric"
            disabled={disabled}
            placeholder={placeholder}
            value={field.value ?? ""}
            onChange={(e) =>
              field.onChange(e.target.value.replace(/[^\d]/g, ""))
            }
            onBlur={field.onBlur}
            name={field.name}
            ref={field.ref}
            className={`flex-1 h-10 ${
              fieldState.error ? "border-red-500" : ""
            } ${inputClassName}`}
          />
        )}
      />
    </div>
  );
}
